import React, { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';
import api from '../services/api';

interface PronumberDetails {
  movetype: string;
  container: string;
  bol: string;
  billto: string;
  customer: string;
}

interface AttachPronumberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (pronumber: string) => Promise<void>;
  chassisno: string;
  // Base path for the pronumber details lookup. Defaults to the yard router
  // — the only caller now. Full URL is `${basePath}/{pronumber}/details`.
  pronumberDetailsBasePath?: string;
}

const AttachPronumberModal: React.FC<AttachPronumberModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  chassisno,
  pronumberDetailsBasePath = '/yard/pronumber',
}) => {
  const [pronumber, setPronumber] = useState('');
  const [pronumberDetails, setPronumberDetails] = useState<PronumberDetails | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isAttaching, setIsAttaching] = useState(false);

  const handleSearch = async () => {
    if (!pronumber.trim()) {
      setSearchError('Please enter a pronumber');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setPronumberDetails(null);

    try {
      const response = await api.get(`${pronumberDetailsBasePath}/${pronumber.trim()}/details`);
      setPronumberDetails(response.data);
    } catch (error: any) {
      console.error('Error searching pronumber:', error);
      if (error.response?.status === 404) {
        setSearchError('Pronumber not found');
      } else {
        setSearchError(error.response?.data?.detail || 'Failed to search pronumber');
      }
    } finally {
      setIsSearching(false);
    }
  };

  const handleConfirm = async () => {
    if (!pronumberDetails) return;

    setIsAttaching(true);
    try {
      await onConfirm(pronumber.trim());
      handleClose();
    } catch (error) {
      console.error('Error attaching pronumber:', error);
      setSearchError('Failed to attach pronumber');
    } finally {
      setIsAttaching(false);
    }
  };

  const handleCancel = () => {
    setPronumber('');
    setPronumberDetails(null);
    setSearchError(null);
  };

  const handleClose = () => {
    setPronumber('');
    setPronumberDetails(null);
    setSearchError(null);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Alt+S attaches (a real persist), Alt+C closes — both mirroring the footer
  // buttons: no attach until a PRO has been looked up, and neither mid-flight.
  useAltHotkeys(
    {
      S: !pronumberDetails || isAttaching ? null : handleConfirm,
      C: isAttaching ? null : handleClose,
    },
    undefined,
    { layer: isOpen },
  );

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 z-[100] transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-500 dark:bg-gray-900 opacity-75"
            onClick={handleClose}
          ></div>
        </div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
          &#8203;
        </span>

        {/* Modal panel */}
        <div
          ref={panelRef}
          style={panelStyle}
          className="relative z-[101] inline-block align-bottom bg-panel rounded-lg text-left overflow-hidden shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
        >
          <ModalHeader
            title={`Attach Pronumber to Chassis ${chassisno}`}
            onClose={handleClose}
            dragHandleProps={dragHandleProps}
          />
          {/* Body */}
          <div className="bg-panel px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="mt-3 space-y-4">
              {/* Pronumber Input */}
              <div>
                <label className="block text-sm font-medium text-label mb-2">
                  Enter Pronumber
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={pronumber}
                    onChange={(e) => setPronumber(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Enter pronumber..."
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    disabled={isSearching || isAttaching}
                  />
                  <button
                    onClick={handleSearch}
                    disabled={isSearching || isAttaching || !pronumber.trim()}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <MagnifyingGlassIcon className="h-5 w-5" />
                    {isSearching ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {searchError && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                  <p className="text-sm text-red-600 dark:text-red-400">{searchError}</p>
                </div>
              )}

              {/* Pronumber Details */}
              {pronumberDetails && (
                <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-md border border-rule">
                  <h4 className="text-sm font-medium text-heading mb-3">
                    Pronumber Details
                  </h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Move Type:</span>
                      <span className="text-heading font-medium">
                        {pronumberDetails.movetype || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Container:</span>
                      <span className="text-heading font-medium">
                        {pronumberDetails.container || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">BOL:</span>
                      <span className="text-heading font-medium">
                        {pronumberDetails.bol || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Bill To:</span>
                      <span className="text-heading font-medium">
                        {pronumberDetails.billto || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Customer:</span>
                      <span className="text-heading font-medium">
                        {pronumberDetails.customer || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer with buttons */}
          <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            {pronumberDetails ? (
              <>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isAttaching}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isAttaching ? 'Attaching...' : 'Confirm Attach'}
                </button>
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isAttaching}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-panel text-base font-medium text-label hover:bg-highlight focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Try Another
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={handleClose}
                className="w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-panel text-base font-medium text-label hover:bg-highlight focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AttachPronumberModal;
